import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Clickable "vX.Y" that opens the release notes. Safe to render inside an <a>:
// the click is stopped so the surrounding link does not navigate.
export const VersionBadge = ({ version }) => {
  const [open, setOpen] = useState(false);
  const [releases, setReleases] = useState(null);

  useEffect(() => {
    if (!open || releases) return;
    fetch(`${API_URL}/api/public/changelog`)
      .then((r) => r.json())
      .then((d) => setReleases(d.releases || []))
      .catch(() => setReleases([]));
  }, [open, releases]);

  if (!version) return null;

  return (
    <>
      <span
        role="button"
        tabIndex={0}
        title="What's new in this version"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(true); } }}
        style={{ textDecoration: 'underline dotted', cursor: 'pointer', marginLeft: '0.3em' }}
        data-testid="version-badge"
      >
        v{version}
      </span>
      {open && createPortal(
        <div
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(false); }}
          style={{
            position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(15,23,42,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
            textAlign: 'left', cursor: 'default'
          }}
          data-testid="changelog-overlay"
        >
          <div
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
            style={{
              background: 'white', color: '#1e293b', borderRadius: '12px', width: '100%', maxWidth: '520px',
              maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 20px 50px rgba(0,0,0,0.35)', fontWeight: 'normal'
            }}
            data-testid="changelog-modal"
          >
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '0.9rem 1.1rem', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0, background: 'white'
            }}>
              <span style={{ fontWeight: 700, fontSize: '1rem', color: '#2c5f2d' }}>What's New</span>
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(false); }}
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#64748b', display: 'flex' }}
                aria-label="Close"
                data-testid="changelog-close-button"
              >
                <X size={18} />
              </button>
            </div>
            <div style={{ padding: '0.75rem 1.1rem 1.1rem' }}>
              {releases === null && <p style={{ fontSize: '0.85rem', color: '#64748b' }}>Loading…</p>}
              {releases && releases.length === 0 && <p style={{ fontSize: '0.85rem', color: '#64748b' }}>No release notes yet.</p>}
              {releases && releases.map((rel, idx) => (
                <div key={rel.version} style={{ marginTop: idx === 0 ? 0 : '1rem' }} data-testid={`changelog-release-${rel.version}`}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem' }}>
                    <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>v{rel.version}</span>
                    {idx === 0 && (
                      <span style={{ fontSize: '0.65rem', background: '#dcfce7', color: '#166534', padding: '1px 6px', borderRadius: '999px', fontWeight: 600 }}>
                        current
                      </span>
                    )}
                    <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{rel.date}</span>
                  </div>
                  <ul style={{ margin: '0.35rem 0 0', paddingLeft: '1.1rem', fontSize: '0.85rem', lineHeight: 1.5, listStyle: 'disc' }}>
                    {rel.items.map((it, i) => <li key={i}>{it}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default VersionBadge;
